// Operator admin for the Signal Orchestrator (DEV-16226). The single
// surface for "turn the dedup layer on, configure it, monitor it":
//
//   - SETTINGS card: org-wide kill-switch, enforce/dry-run mode, optional
//     signal-age cutoff. Patches `/v1/org-settings`; the orchestrator
//     reads it on every decision, so changes take effect immediately.
//   - STATS card: dedup rate, signal counts, decisions split by mode +
//     enforcement, plus orchestrator worker liveness. Empty `workers` is the
//     red-flag UI ("orchestrator is down").
//   - RECENT DECISIONS list: last 25 deduplicated runs with mode + reason,
//     for spot-checking that the rule is matching the right things during a
//     dry-run rollout.
//
// Read-only operator surface (no destructive ops) — settings are reversible.
import { useClient } from '@cortex/react-query';
import type { DedupDecisionMode, OrchestratorStatsResponse, OrgSettings } from '@cortex/types';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { OrchestratorTabs } from '../../components/orchestrator/OrchestratorTabs';
import { formatRelative } from '../../lib/format';

export const Route = createFileRoute('/orchestrator/')({ component: OrchestratorPage });

const cardGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: 'var(--sp-4)',
};
const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};
const kpiRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 'var(--sp-3)',
};
const decisionRowStyle: CSSProperties = {
  // LAYER · MODE · ACTION · WORKFLOW · REASON · WHEN
  display: 'grid',
  gridTemplateColumns: '52px 52px 72px 150px 1fr 130px',
  gap: 'var(--sp-2)',
  alignItems: 'baseline',
  padding: 'var(--sp-2) 0',
  borderTop: '1px solid var(--border-subtle)',
};

function OrchestratorPage() {
  const client = useClient();
  const qc = useQueryClient();

  const [settingsQ, statsQ] = useQueries({
    queries: [
      {
        queryKey: ['org-settings'],
        queryFn: () => client.http.get('v1/org-settings').json<OrgSettings>(),
      },
      {
        queryKey: ['orchestrator', 'stats', 24],
        queryFn: () =>
          client.http
            .get('v1/orchestrator/stats', { searchParams: { windowHours: 24 } })
            .json<OrchestratorStatsResponse>(),
        refetchInterval: 10_000,
      },
    ],
  });

  if (settingsQ.isLoading) return <Spinner />;
  if (settingsQ.error) {
    return (
      <div className="alert alert--danger">
        Failed to load org settings: {(settingsQ.error as Error).message}
      </div>
    );
  }
  const settings = settingsQ.data;
  if (!settings) return <Spinner />;
  const stats = statsQ.data;
  const liveLeader = stats?.workers.find((w) => w.lastSeenAt) ?? null;

  return (
    <>
      <OrchestratorTabs containerStyle={{ marginBottom: 'var(--sp-4)' }} />

      <PageHeader title="Deduplication" />

      <div style={cardGridStyle}>
        <SettingsCard
          settings={settings}
          onSaved={async () => {
            await Promise.all([
              qc.invalidateQueries({ queryKey: ['org-settings'] }),
              qc.invalidateQueries({ queryKey: ['orchestrator', 'stats', 24] }),
            ]);
          }}
        />
        <StatsCard stats={stats} liveLeader={liveLeader} />
      </div>

      <div className="card" style={{ marginTop: 'var(--sp-4)' }}>
        <div className="card-header">
          <span className="t-mono-label">RECENT DECISIONS · LAST 100</span>
          <h3 className="t-h3">What the orchestrator decided</h3>
        </div>
        <div className="card-body">
          <RecentDecisions stats={stats} />
        </div>
      </div>
    </>
  );
}

function SettingsCard({
  settings,
  onSaved,
}: {
  settings: OrgSettings;
  onSaved: () => Promise<void>;
}) {
  const client = useClient();
  const [dedupEnabled, setDedupEnabled] = useState(settings.dedupEnabled);
  const [dedupMode, setDedupMode] = useState<DedupDecisionMode>(settings.dedupMode);
  const [maxAge, setMaxAge] = useState<string>(
    settings.maxSignalAgeHours === null ? '' : String(settings.maxSignalAgeHours),
  );
  // Layer 2 — semantic dedup. Threshold rendered as a percent slider; the
  // value sent to the API is basis points (0..10000) to match the schema.
  const [l2Enabled, setL2Enabled] = useState(settings.dedupL2Enabled);
  const [l2ThresholdBp, setL2ThresholdBp] = useState(settings.dedupL2ThresholdBp);
  const [l2EmbeddingModel, setL2EmbeddingModel] = useState(settings.dedupL2EmbeddingModel);

  // Layer 3 — LLM-agent dedup judge. Budget is stored in USD cents on the
  // wire; rendered as dollars in the UI for readability.
  const [l3Enabled, setL3Enabled] = useState(settings.dedupL3Enabled);
  const [l3AgentId, setL3AgentId] = useState<string | null>(settings.dedupL3AgentId);
  const [l3BudgetUsd, setL3BudgetUsd] = useState<string>(
    (settings.dedupL3MaxDailyUsdCents / 100).toFixed(2),
  );

  // Agent picker — only fetched when the L3 section is in view (rendered
  // when dedupEnabled). Cached, refetched-on-mount.
  const agents = useQuery({
    queryKey: ['agents', 'l3-judge-picker'],
    queryFn: () =>
      client.http
        .get('v1/agents?limit=200')
        .json<{ items: Array<{ id: string; name: string; modelConfigId: string | null }> }>(),
    enabled: dedupEnabled,
    staleTime: 60_000,
  });

  // Re-sync local form from server on refetch so a remote update flows through.
  useEffect(() => {
    setDedupEnabled(settings.dedupEnabled);
    setDedupMode(settings.dedupMode);
    setMaxAge(settings.maxSignalAgeHours === null ? '' : String(settings.maxSignalAgeHours));
    setL2Enabled(settings.dedupL2Enabled);
    setL2ThresholdBp(settings.dedupL2ThresholdBp);
    setL2EmbeddingModel(settings.dedupL2EmbeddingModel);
    setL3Enabled(settings.dedupL3Enabled);
    setL3AgentId(settings.dedupL3AgentId);
    setL3BudgetUsd((settings.dedupL3MaxDailyUsdCents / 100).toFixed(2));
  }, [
    settings.dedupEnabled,
    settings.dedupMode,
    settings.maxSignalAgeHours,
    settings.dedupL2Enabled,
    settings.dedupL2ThresholdBp,
    settings.dedupL2EmbeddingModel,
    settings.dedupL3Enabled,
    settings.dedupL3AgentId,
    settings.dedupL3MaxDailyUsdCents,
  ]);

  // L3 budget input → cents. Empty / non-numeric blocks save.
  const trimmedBudget = l3BudgetUsd.trim();
  const parsedBudgetUsd = trimmedBudget === '' ? NaN : Number(trimmedBudget);
  const budgetInvalid =
    trimmedBudget === '' || Number.isNaN(parsedBudgetUsd) || parsedBudgetUsd < 0;
  const l3BudgetCents = budgetInvalid ? 5000 : Math.round(parsedBudgetUsd * 100);
  const l3NeedsAgent = l3Enabled && !l3AgentId;

  const trimmedAge = maxAge.trim();
  const parsedAge = trimmedAge === '' ? null : Number(trimmedAge);
  const ageInvalid =
    trimmedAge !== '' &&
    (Number.isNaN(parsedAge) || !Number.isInteger(parsedAge) || (parsedAge ?? 0) <= 0);

  const save = useMutation({
    mutationFn: () =>
      client.http
        .patch('v1/org-settings', {
          json: {
            dedupEnabled,
            dedupMode,
            maxSignalAgeHours: parsedAge,
            dedupL2Enabled: l2Enabled,
            dedupL2ThresholdBp: l2ThresholdBp,
            dedupL2EmbeddingModel: l2EmbeddingModel,
            dedupL3Enabled: l3Enabled,
            dedupL3AgentId: l3AgentId,
            dedupL3MaxDailyUsdCents: l3BudgetCents,
          },
        })
        .json<OrgSettings>(),
    onSuccess: onSaved,
  });

  // Tabbed layout: keeps the card height bounded as L2/L3 settings grow.
  // All state lives at the parent level, so `save.mutate()` always sends
  // every field — switching tabs doesn't drop any pending edits.
  const [activeTab, setActiveTab] = useState<'L1' | 'L2' | 'L3'>('L1');

  // Cross-tab save blockers. If the user is on the L1 tab but the L3 tab
  // has an invalid config, we surface a banner that names the offending
  // tab + offers a one-click jump.
  const tabIssues: Array<{ tab: 'L1' | 'L2' | 'L3'; message: string }> = [];
  if (ageInvalid)
    tabIssues.push({ tab: 'L1', message: 'Max signal age must be a positive integer.' });
  if (budgetInvalid)
    tabIssues.push({ tab: 'L3', message: 'Daily budget must be a non-negative number.' });
  if (l3NeedsAgent)
    tabIssues.push({ tab: 'L3', message: 'Layer 3 is on but no judge agent is selected.' });
  const blockingIssue = tabIssues.find((i) => i.tab !== activeTab);

  return (
    <div className="card">
      <div className="card-header">
        <span className="t-mono-label">SETTINGS</span>
        <h3 className="t-h3">Layer controls</h3>
      </div>
      <div
        className="card-body"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
      >
        <LayerTabs
          activeTab={activeTab}
          onTab={setActiveTab}
          l1Active={dedupEnabled}
          l2Active={dedupEnabled && l2Enabled}
          l3Active={dedupEnabled && l3Enabled}
          tabIssues={tabIssues}
        />

        {activeTab === 'L1' ? (
          <Layer1Panel
            dedupEnabled={dedupEnabled}
            setDedupEnabled={setDedupEnabled}
            dedupMode={dedupMode}
            setDedupMode={setDedupMode}
            maxAge={maxAge}
            setMaxAge={setMaxAge}
            ageInvalid={ageInvalid}
          />
        ) : null}

        {activeTab === 'L2' ? (
          <Layer2Panel
            dedupEnabled={dedupEnabled}
            l2Enabled={l2Enabled}
            setL2Enabled={setL2Enabled}
            l2ThresholdBp={l2ThresholdBp}
            setL2ThresholdBp={setL2ThresholdBp}
            l2EmbeddingModel={l2EmbeddingModel}
            setL2EmbeddingModel={setL2EmbeddingModel}
          />
        ) : null}

        {activeTab === 'L3' ? (
          <Layer3Panel
            dedupEnabled={dedupEnabled}
            l3Enabled={l3Enabled}
            setL3Enabled={setL3Enabled}
            l3AgentId={l3AgentId}
            setL3AgentId={setL3AgentId}
            l3BudgetUsd={l3BudgetUsd}
            setL3BudgetUsd={setL3BudgetUsd}
            agents={agents.data?.items ?? []}
            agentsLoading={agents.isLoading}
            l3NeedsAgent={l3NeedsAgent}
            budgetInvalid={budgetInvalid}
          />
        ) : null}

        {blockingIssue ? (
          <div
            className="alert alert--warning"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-2)',
            }}
          >
            <span className="t-xs">
              Save is blocked by an issue on <strong>{blockingIssue.tab}</strong>:{' '}
              {blockingIssue.message}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setActiveTab(blockingIssue.tab)}
            >
              Go to {blockingIssue.tab}
            </button>
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            alignItems: 'center',
            paddingTop: 'var(--sp-3)',
            borderTop: '1px solid var(--border-subtle)',
          }}
        >
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => save.mutate()}
            disabled={save.isPending || ageInvalid || budgetInvalid || l3NeedsAgent}
          >
            {save.isPending ? 'Saving…' : 'Save settings'}
          </button>
          {save.error ? (
            <span className="t-xs" style={{ color: 'var(--status-danger, var(--text-danger))' }}>
              {(save.error as Error).message}
            </span>
          ) : save.isSuccess ? (
            <span className="t-xs t-muted">Saved — takes effect on next decision.</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ── Layer tab strip ──────────────────────────────────────────────────

function LayerTabs(props: {
  activeTab: 'L1' | 'L2' | 'L3';
  onTab: (t: 'L1' | 'L2' | 'L3') => void;
  l1Active: boolean;
  l2Active: boolean;
  l3Active: boolean;
  tabIssues: Array<{ tab: 'L1' | 'L2' | 'L3'; message: string }>;
}) {
  const issueTabs = new Set(props.tabIssues.map((i) => i.tab));
  const tabs: Array<{ id: 'L1' | 'L2' | 'L3'; label: string; sub: string; on: boolean }> = [
    { id: 'L1', label: 'Layer 1', sub: 'Deterministic', on: props.l1Active },
    { id: 'L2', label: 'Layer 2', sub: 'Semantic', on: props.l2Active },
    { id: 'L3', label: 'Layer 3', sub: 'LLM judge', on: props.l3Active },
  ];
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        gap: 'var(--sp-1)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      {tabs.map((t) => {
        const isActive = t.id === props.activeTab;
        const hasIssue = issueTabs.has(t.id);
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => props.onTab(t.id)}
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'transparent',
              border: 'none',
              borderBottom: isActive
                ? '2px solid var(--horizon-glow, var(--text-primary))'
                : '2px solid transparent',
              marginBottom: '-1px',
              cursor: 'pointer',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {t.label}
              <span
                aria-hidden={true}
                title={t.on ? 'enabled' : 'disabled'}
                style={{
                  display: 'inline-block',
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: t.on
                    ? 'var(--status-success, #4ade80)'
                    : 'var(--text-secondary, #888)',
                }}
              />
              {hasIssue ? (
                <span
                  aria-label="needs attention"
                  title="Needs attention"
                  style={{
                    display: 'inline-block',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: 'var(--status-warning, #f59e0b)',
                  }}
                />
              ) : null}
            </span>
            <span className="t-xs t-muted">{t.sub}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Layer 1 panel ────────────────────────────────────────────────────

function Layer1Panel(props: {
  dedupEnabled: boolean;
  setDedupEnabled: (v: boolean) => void;
  dedupMode: DedupDecisionMode;
  setDedupMode: (v: DedupDecisionMode) => void;
  maxAge: string;
  setMaxAge: (v: string) => void;
  ageInvalid: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <label className="t-secondary" style={{ margin: 0 }}>
            Kill-switch
          </label>
          <LayerHelp
            title="Layer 1 — Deterministic key match"
            how="Compares the new run's signal key against (1) currently OPEN signals and (2) other in-flight runs in the same org. Pure string equality, no AI involved. Runs first because it's free and ~5ms."
            example={[
              'A run arrives with key `fab:robot-42:lidar_drift`.',
              'L1 finds an OPEN signal with the SAME key → cancels the new run (Mode A — matched-signal).',
              'No matching signal, but another run with the same key is RUNNING → cancels as duplicate of in-flight run (Mode B — matched-run).',
              'No matching signal, no in-flight run → L1 misses, chain falls through to L2.',
            ]}
            cost="$0 · ~5ms"
          />
        </div>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            className="checkbox"
            checked={props.dedupEnabled}
            onChange={(e) => props.setDedupEnabled(e.target.checked)}
          />
          <span className="t-sm">Dedup layer is {props.dedupEnabled ? 'ON' : 'OFF'}</span>
        </label>
        <span className="t-xs t-muted">
          When off, the orchestrator ignores this org entirely. Runs proceed unconditionally. This
          switch overrides every layer.
        </span>
      </div>

      <div style={sectionStyle}>
        <label className="t-secondary">Decision mode</label>
        <select
          className="input"
          value={props.dedupMode}
          onChange={(e) => props.setDedupMode(e.target.value as DedupDecisionMode)}
          disabled={!props.dedupEnabled}
        >
          <option value="enforce">Enforce — cancel duplicate runs</option>
          <option value="dry-run">Dry-run — record decisions, do NOT cancel</option>
        </select>
        <span className="t-xs t-muted">
          Use dry-run during rollout to verify keys + matches across real traffic before enabling
          cancellation.
        </span>
      </div>

      <div style={sectionStyle}>
        <label className="t-secondary" htmlFor="max-age">
          Max signal age (hours)
        </label>
        <input
          id="max-age"
          type="text"
          inputMode="numeric"
          className="input"
          placeholder="Leave blank for no cutoff"
          value={props.maxAge}
          onChange={(e) => props.setMaxAge(e.target.value)}
          disabled={!props.dedupEnabled}
          aria-invalid={props.ageInvalid}
        />
        {props.ageInvalid ? (
          <span className="t-xs" style={{ color: 'var(--status-danger, var(--text-danger))' }}>
            Must be a positive integer (or blank for no cutoff).
          </span>
        ) : (
          <span className="t-xs t-muted">
            Signals older than this are skipped for matching (every layer). Blank = no cutoff.
          </span>
        )}
      </div>
    </div>
  );
}

// ── Layer 2 panel ────────────────────────────────────────────────────

// Curated list of embedding models the orchestrator can actually use.
// The pgvector column is sized 1536 — adding a 3072-dim model (e.g.
// text-embedding-3-large) needs a schema migration, so we don't expose
// it here. Operators who need it can patch via the API directly.
const l2EmbeddingModelOptions: Array<{ value: string; label: string; description: string }> = [
  {
    value: 'text-embedding-3-small',
    label: 'OpenAI · text-embedding-3-small',
    description: '1536 dims · default · ~$0.02 / 1M tokens',
  },
  {
    value: 'text-embedding-ada-002',
    label: 'OpenAI · text-embedding-ada-002',
    description: '1536 dims · legacy · ~$0.10 / 1M tokens',
  },
];

function Layer2Panel(props: {
  dedupEnabled: boolean;
  l2Enabled: boolean;
  setL2Enabled: (v: boolean) => void;
  l2ThresholdBp: number;
  setL2ThresholdBp: (v: number) => void;
  l2EmbeddingModel: string;
  setL2EmbeddingModel: (v: string) => void;
}) {
  const disabled = !props.dedupEnabled || !props.l2Enabled;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <label className="t-secondary" style={{ margin: 0 }}>
              Layer 2 — Semantic dedup
            </label>
            <LayerHelp
              title="Layer 2 — Probabilistic / semantic"
              how="Embeds the candidate run's text (workflow name + signal key + input JSON) using an embedding model, then queries pgvector for the nearest open signal by cosine similarity. If the similarity is at or above the threshold, it counts as a match. Always Mode A (signals only — there's no embedding for in-flight runs)."
              example={[
                'Open signal: key `fab:robot-42:cooling_loop_fault`, summary "Cooling loop fault — CPU at thermal limit".',
                'A new run arrives with key `fab:robot-42:cpu_thermal_throttle` and input `{symptom: "CPU running too hot, throttling"}`.',
                'L1 misses (different key). L2 embeds both texts → cosine similarity 0.84 (above the 0.78 threshold) → match.',
                'The new run is cancelled with `dedupReason: "semantic match to signal … (similarity 0.840)"`, layer = L2, confidence = 0.84.',
              ]}
              cost="~$0.0001/call · ~50ms"
            />
          </div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              className="checkbox"
              checked={props.l2Enabled}
              disabled={!props.dedupEnabled}
              onChange={(e) => props.setL2Enabled(e.target.checked)}
            />
            <span className="t-sm">{props.l2Enabled ? 'On' : 'Off'}</span>
          </label>
        </div>
        <span className="t-xs t-muted">
          pgvector cosine over open signals. Catches duplicates whose signal key differs but whose
          meaning matches (e.g. an existing investigation about overheating + a new CPU-throttle
          alert on the same robot). Runs after L1; falls through silently when L1 already matched.
        </span>
        {/*
         * Anthropic doesn't offer an embeddings API — L2 needs a separate
         * provider (OpenAI is the default; Voyage / Cohere / Mistral also
         * work via the appropriate env var). Operators running an
         * Anthropic-only stack will see L2 silently no-op without a key
         * configured; this banner is the only place that calls it out.
         */}
        <div
          className="alert alert--info"
          style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--fz-xs)' }}
        >
          <strong>Requires an embedding-provider key.</strong> Anthropic does not provide an
          embeddings API. Set <code>OPENAI_API_KEY</code> on the orchestrator pod to use OpenAI
          (default — <code>text-embedding-3-small</code>, ~$0.02 / 1M tokens). Without it, L2
          silently no-ops; L1 + L3 still work.
        </div>
      </div>

      <div style={sectionStyle}>
        <label
          className="t-secondary t-sm"
          htmlFor="l2-threshold"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
        >
          <span>Similarity threshold</span>
          <span className="t-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {(props.l2ThresholdBp / 100).toFixed(1)}%
          </span>
        </label>
        <input
          id="l2-threshold"
          type="range"
          min={5000}
          max={9800}
          step={50}
          value={props.l2ThresholdBp}
          disabled={disabled}
          onChange={(e) => props.setL2ThresholdBp(Number(e.target.value))}
          style={{ width: '100%' }}
        />
        <span className="t-xs t-muted">
          Higher = stricter. The default 78% catches obvious semantic matches without
          over-deduplicating loosely-related alerts.
        </span>
      </div>

      <div style={sectionStyle}>
        <label className="t-secondary t-sm" htmlFor="l2-model">
          Embedding model
        </label>
        <select
          id="l2-model"
          className="input"
          value={props.l2EmbeddingModel}
          disabled={disabled}
          onChange={(e) => props.setL2EmbeddingModel(e.target.value)}
        >
          {l2EmbeddingModelOptions.some((o) => o.value === props.l2EmbeddingModel) ? null : (
            // Surface a stale / custom value (e.g. set via API) so the
            // operator sees it instead of the dropdown silently
            // resetting to the first option.
            <option value={props.l2EmbeddingModel}>{props.l2EmbeddingModel} (custom)</option>
          )}
          {l2EmbeddingModelOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="t-xs t-muted">
          {l2EmbeddingModelOptions.find((o) => o.value === props.l2EmbeddingModel)?.description ??
            'Custom value (set via API). Must be a 1536-dim model to match the pgvector column.'}{' '}
          Changing the model requires re-embedding open signals.
        </span>
      </div>
    </div>
  );
}

// ── Layer 3 panel ────────────────────────────────────────────────────

function Layer3Panel(props: {
  dedupEnabled: boolean;
  l3Enabled: boolean;
  setL3Enabled: (v: boolean) => void;
  l3AgentId: string | null;
  setL3AgentId: (v: string | null) => void;
  l3BudgetUsd: string;
  setL3BudgetUsd: (v: string) => void;
  agents: Array<{ id: string; name: string; modelConfigId: string | null }>;
  agentsLoading: boolean;
  l3NeedsAgent: boolean;
  budgetInvalid: boolean;
}) {
  const disabled = !props.dedupEnabled || !props.l3Enabled;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <label className="t-secondary" style={{ margin: 0 }}>
              Layer 3 — LLM-agent judge
            </label>
            <LayerHelp
              placement="right"
              title="Layer 3 — LLM-agent reasoned dedup"
              how="The customer-selected Agent receives the candidate run and the set of OPEN signals as structured context, then returns a strict {duplicate, signalId, confidence, reasoning} decision. The orchestrator only acts when the returned signalId is one of the inputs (hallucinated ids are rejected). The judge's instructions are the agent's system prompt; the output schema is fixed by the orchestrator. Gated by a daily USD budget."
              example={[
                'Open signal: "Cooling loop fault — CPU at thermal limit on robot-42".',
                'A new run arrives with `{symptom: "task latency higher than baseline on robot-42"}` — key `fab:robot-42:task_latency_high`.',
                'L1 misses (different key). L2 embeds → cosine 0.62 (BELOW threshold, miss).',
                'L3 sends both to the judge agent → returns `{duplicate: true, signalId: <cooling-signal-id>, confidence: 0.91, reasoning: "Task latency on a robot with an active cooling investigation is most likely caused by thermal throttling — same root cause."}`',
                'Orchestrator cancels the run, links to the cooling signal, records layer = L3, confidence = 0.91.',
              ]}
              cost="~$0.01–0.05/call · ~1–3s · daily-budget capped"
            />
          </div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              className="checkbox"
              checked={props.l3Enabled}
              disabled={!props.dedupEnabled}
              onChange={(e) => props.setL3Enabled(e.target.checked)}
            />
            <span className="t-sm">{props.l3Enabled ? 'On' : 'Off'}</span>
          </label>
        </div>
        <span className="t-xs t-muted">
          The selected Agent receives the candidate run + open signals and returns a structured
          duplicate / not-duplicate decision with confidence. Runs after L1 + L2. Heaviest layer —
          most useful for high-value workflows where a missed dedup is expensive.
        </span>
      </div>

      <div style={sectionStyle}>
        <label className="t-secondary t-sm" htmlFor="l3-agent">
          Judge agent
        </label>
        <select
          id="l3-agent"
          className="input"
          value={props.l3AgentId ?? ''}
          disabled={disabled}
          onChange={(e) => props.setL3AgentId(e.target.value === '' ? null : e.target.value)}
          aria-invalid={props.l3NeedsAgent}
        >
          <option value="">
            {props.agentsLoading ? '— Loading agents… —' : '— Select an agent —'}
          </option>
          {props.agents.map((a) => (
            <option key={a.id} value={a.id} disabled={!a.modelConfigId}>
              {a.name}
              {a.modelConfigId ? '' : ' (no model config)'}
            </option>
          ))}
        </select>
        {props.l3NeedsAgent ? (
          <span className="t-xs" style={{ color: 'var(--status-danger, var(--text-danger))' }}>
            Layer 3 needs an agent. Pick one or turn the layer off.
          </span>
        ) : (
          <span className="t-xs t-muted">
            The agent&apos;s <code>instructions</code> field becomes the judge&apos;s system prompt;
            the orchestrator supplies a strict output schema. For best results, use an agent with
            <em> no tools</em>, conservative-match instructions, and a fast/cheap model.
          </span>
        )}
      </div>

      <div style={sectionStyle}>
        <label className="t-secondary t-sm" htmlFor="l3-budget">
          Daily budget (USD)
        </label>
        <input
          id="l3-budget"
          type="text"
          inputMode="decimal"
          className="input"
          placeholder="50.00"
          value={props.l3BudgetUsd}
          disabled={disabled}
          onChange={(e) => props.setL3BudgetUsd(e.target.value)}
          aria-invalid={props.budgetInvalid}
        />
        {props.budgetInvalid ? (
          <span className="t-xs" style={{ color: 'var(--status-danger, var(--text-danger))' }}>
            Must be a non-negative number (USD).
          </span>
        ) : (
          <span className="t-xs t-muted">
            Hard cap on L3 LLM spend per UTC day. Once exceeded, L3 is skipped (the run falls
            through to no-match) until midnight UTC.
          </span>
        )}
      </div>
    </div>
  );
}

function StatsCard({
  stats,
  liveLeader,
}: {
  stats: OrchestratorStatsResponse | undefined;
  liveLeader: { podId: string; lastSeenAt: string } | null;
}) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="t-mono-label">LAST 24 HOURS · LIVE</span>
        <h3 className="t-h3">Stats</h3>
      </div>
      <div
        className="card-body"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
      >
        {!stats ? (
          <Spinner />
        ) : (
          <>
            <div style={kpiRowStyle}>
              <Kpi label="Dedup rate" value={formatPct(stats.runs.dedupRate)} />
              <Kpi
                label="Runs deduplicated"
                value={`${stats.runs.deduplicated} / ${stats.runs.total}`}
              />
              <Kpi label="Signals opened" value={String(stats.signals.opened)} />
              <Kpi label="Open now" value={String(stats.signals.openCount)} />
              <Kpi
                label="Mode A · B"
                value={`${stats.decisionsByMode.A} · ${stats.decisionsByMode.B}`}
              />
              <Kpi
                label="L1 · L2 · L3"
                value={`${stats.decisionsByLayer.L1} · ${stats.decisionsByLayer.L2} · ${stats.decisionsByLayer.L3}`}
              />
              <Kpi
                label="Enforced · dry-run"
                value={`${stats.decisionsByEnforcement.enforced} · ${stats.decisionsByEnforcement.dryRun}`}
              />
            </div>
            <div style={sectionStyle}>
              <span className="t-mono-label">ORCHESTRATOR LIVENESS</span>
              {liveLeader ? (
                <span className="t-sm">
                  <span className="badge badge--success">UP</span>
                  &nbsp;{liveLeader.podId} · last beat {formatRelative(liveLeader.lastSeenAt)}
                </span>
              ) : (
                <span
                  className="t-sm"
                  style={{ color: 'var(--status-danger, var(--text-danger))' }}
                >
                  <span className="badge badge--danger">DOWN</span>
                  &nbsp;No orchestrator heartbeat in Redis. Decisions are not being made.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RecentDecisions({ stats }: { stats: OrchestratorStatsResponse | undefined }) {
  // Client-side layer filter — the endpoint already caps at 25 rows server-
  // side, so we don't add pagination here (this is a "live feed" surface;
  // deep history lives on /orchestrator/analytics). Filtering by layer lets
  // an operator zoom into the L2/L3 calls without scrolling past dozens of
  // identical L1 hits during a burst.
  const [layerFilter, setLayerFilter] = useState<'all' | 'L1' | 'L2' | 'L3'>('all');

  if (!stats) return <Spinner />;
  if (stats.recentDecisions.length === 0) {
    return <span className="t-sm t-muted">No decisions in the window.</span>;
  }

  const filtered =
    layerFilter === 'all'
      ? stats.recentDecisions
      : stats.recentDecisions.filter((d) => d.layer === layerFilter);
  const counts = {
    all: stats.recentDecisions.length,
    L1: stats.recentDecisions.filter((d) => d.layer === 'L1').length,
    L2: stats.recentDecisions.filter((d) => d.layer === 'L2').length,
    L3: stats.recentDecisions.filter((d) => d.layer === 'L3').length,
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-1)',
          marginBottom: 'var(--sp-2)',
          flexWrap: 'wrap',
        }}
      >
        {(['all', 'L1', 'L2', 'L3'] as const).map((key) => {
          const active = key === layerFilter;
          return (
            <button
              key={key}
              type="button"
              className={`btn btn--sm ${active ? 'btn--primary' : ''}`}
              onClick={() => setLayerFilter(key)}
              aria-pressed={active}
            >
              {key === 'all' ? 'All' : key}
              <span className="t-xs t-muted" style={{ marginLeft: 'var(--sp-1)' }}>
                {counts[key]}
              </span>
            </button>
          );
        })}
      </div>

      <div
        style={{
          ...decisionRowStyle,
          borderTop: 'none',
          paddingBottom: 'var(--sp-1)',
          // Header stays pinned while the list below scrolls.
          position: 'sticky',
          top: 0,
          background: 'var(--surface)',
          zIndex: 1,
        }}
      >
        <span className="t-mono-label">LAYER</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span className="t-mono-label">MODE</span>
          <LayerHelp
            title="Mode — what the orchestrator matched against"
            how="Independent of which engine made the call, every dedup decision falls into one of two structural modes. Mode A is the steady-state pattern (a long-lived investigation is already open for this issue); Mode B is the burst-protection pattern (two runs raced before any investigation had time to open an signal)."
            example={[
              'Mode A — matched-signal. An OPEN signal already exists for the issue. New run arrives → orchestrator finds the signal → cancels the run + links it to the signal. The investigation continues uninterrupted.',
              'Mode B — matched-in-flight-run. Two alarms arrive within ~100ms. The first run is still RUNNING (investigating) when the second arrives. No signal exists yet. The orchestrator picks the earlier run as the winner and cancels the later one. After the winner completes + opens an signal, future alarms would be Mode A instead.',
              'Mode A is what catches the long-tail (same problem firing every 5 min for hours). Mode B catches the burst (webhook retries, queue replays). Only L1 produces Mode B; L2 and L3 are Mode-A-only (they compare against open signals).',
            ]}
          />
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span className="t-mono-label">ACTION</span>
          <LayerHelp
            title="Action — what the orchestrator actually did"
            how="Reflects the org-level `dedupMode` setting at the moment of the decision. ENFORCE is the production posture: a matched run is cancelled, no agent execution, no LLM cost. DRY-RUN is the rollout-observation posture: the decision is recorded (event + dedupReason on the run) but the run is left alone — it executes normally so you can verify the orchestrator would have made the right call before flipping to enforce."
            example={[
              'ENFORCE — Run.status flips to CANCELLED with abortReason = DUPLICATE_OF_OPEN_SIGNAL (or DUPLICATE_OF_IN_FLIGHT_RUN for Mode B). The agent never runs. No LLM tokens spent. The dedup is real.',
              'DRY-RUN — Run.status is untouched. Same event emitted, but `enforced: false` and `dedupReason` is prefixed with `[dry-run]`. The agent runs, the workflow completes normally — you just have the audit trail of "what would have been cancelled" sitting next to the actual outcome.',
              'Switch via Settings tab → Layer 1 → Decision mode. Effect is immediate (orchestrator reads OrgSetting on every decision).',
            ]}
          />
        </span>
        <span className="t-mono-label">WORKFLOW</span>
        <span className="t-mono-label">REASON</span>
        <span className="t-mono-label">WHEN</span>
      </div>
      {/*
       * Card grows up to ~70vh then scrolls inside. Keeps the page from
       * being dominated by 100 rows on a heavy burst while still letting
       * the operator scroll back through the history.
       */}
      <div style={{ maxHeight: '60vh', overflowY: 'auto', minHeight: '160px' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 'var(--sp-3) 0' }}>
            <span className="t-sm t-muted">
              No {layerFilter} decisions in this window. Switch to All to see other layers.
            </span>
          </div>
        ) : (
          filtered.map((d) => (
            <div key={`${d.runId}-${d.at}`} style={decisionRowStyle}>
              <span className={`badge ${layerBadgeClass(d.layer)}`} title={`Layer ${d.layer}`}>
                {d.layer}
              </span>
              <span className={`badge ${d.mode === 'A' ? 'badge--tool' : 'badge--neutral'}`}>
                {d.mode}
              </span>
              <span className={`badge ${d.enforced ? 'badge--success' : 'badge--warning'}`}>
                {d.enforced ? 'enforce' : 'dry'}
              </span>
              <span
                className="t-sm"
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={d.workflowName ?? d.workflowId ?? ''}
              >
                {d.workflowName ?? <span className="t-muted">—</span>}
              </span>
              <span className="t-sm">
                {d.reason}
                {d.confidence !== null ? (
                  <span className="t-xs t-muted" style={{ marginLeft: 'var(--sp-2)' }}>
                    · conf {(d.confidence * 100).toFixed(0)}%
                  </span>
                ) : null}
              </span>
              <span className="t-xs t-muted">{formatRelative(d.at)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div style={sectionStyle}>
      <span className="t-mono-label">{label}</span>
      <span className="t-h2" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

function formatPct(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * Small `(?)` icon next to each layer header. Click toggles a popover with
 * "how it works" + a worked example. Click-toggled (not hover) for keyboard
 * accessibility; click-outside or pressing Escape closes it. The popover is
 * absolutely positioned to the right of the icon so it doesn't reflow the
 * surrounding label row.
 */
function LayerHelp({
  title,
  how,
  example,
  cost,
  placement = 'left',
}: {
  title: string;
  how: string;
  example: string[];
  cost?: string;
  /**
   * Where the popover anchors relative to the icon. `'left'` (default)
   * aligns the popover's left edge with the icon — fine when there's
   * room to the right. `'right'` aligns the popover's right edge with
   * the icon so the body grows leftward instead — used on the L3 tab
   * (rightmost in the strip) where left-aligned popovers would bleed
   * off the card / viewport.
   */
  placement?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on click-outside + Escape so the popover doesn't strand the user.
  useEffect(() => {
    if (!open) return;
    const onClickAway = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label={`Help: ${title}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '1px solid var(--border-subtle)',
          background: open ? 'var(--surface-raised)' : 'transparent',
          color: 'var(--text-secondary)',
          fontSize: 11,
          fontWeight: 600,
          lineHeight: '16px',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        ?
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={title}
          style={{
            position: 'absolute',
            top: '100%',
            ...(placement === 'right' ? { right: 0 } : { left: 0 }),
            marginTop: 6,
            zIndex: 30,
            width: 400,
            maxWidth: '90vw',
            padding: 'var(--sp-3) var(--sp-3)',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-popover, 0 10px 32px rgba(0,0,0,0.4))',
            fontSize: 'var(--fz-xs)',
            color: 'var(--text-primary)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 'var(--sp-2)',
              marginBottom: 'var(--sp-2)',
            }}
          >
            <strong style={{ fontSize: 'var(--fz-sm)' }}>{title}</strong>
            {cost ? (
              <span className="t-mono t-xs t-muted" style={{ whiteSpace: 'nowrap' }}>
                {cost}
              </span>
            ) : null}
          </div>
          <div style={{ marginBottom: 'var(--sp-2)' }}>
            <span className="t-mono-label">HOW IT WORKS</span>
            <p style={{ margin: '4px 0 0', lineHeight: 1.5 }}>{how}</p>
          </div>
          <div>
            <span className="t-mono-label">EXAMPLE</span>
            <ol style={{ margin: '4px 0 0 var(--sp-3)', padding: 0, lineHeight: 1.5 }}>
              {example.map((step, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function layerBadgeClass(layer: 'L1' | 'L2' | 'L3'): string {
  // L1 = green (free + always on), L2 = warning amber (paid embeddings),
  // L3 = info / iris (LLM, heaviest). Falls back to neutral.
  switch (layer) {
    case 'L1':
      return 'badge--success';
    case 'L2':
      return 'badge--warning';
    case 'L3':
      return 'badge--tool';
    default:
      return 'badge--neutral';
  }
}
